/**
 * English translations for Hashee Admin Console.
 * Keys follow the pattern: section.context or section.sub.context
 */
export const en: Record<string, string> = {
  // ─── Common ───
  "common.loading": "Loading...",
  "common.search": "Search",
  "common.apply": "Apply",
  "common.filter": "Filter",
  "common.refresh": "Refresh",
  "common.cancel": "Cancel",
  "common.confirm": "Confirm",
  "common.prev": "Prev",
  "common.next": "Next",
  "common.previous": "Previous",
  "common.view": "View",
  "common.yes": "Yes",
  "common.no": "No",
  "common.date": "Date",
  "common.status": "Status",
  "common.created": "Created",
  "common.action": "Action",
  "common.from": "From",
  "common.to": "To",
  "common.failed_to_load": "Failed to load",
  "common.action_failed": "Action failed",
  "common.no_data_for_period": "No data for this period",
  "common.show_details": "Show Details",
  "common.hide_details": "Hide Details",
  "common.all": "All",
  "stats.total": "total",

  // ─── Sidebar ───
  "sidebar.logo": "Hashee Ops",
  "sidebar.sign_out": "Sign out",
  "nav.dashboard": "Dashboard",
  "nav.metrics": "Metrics",
  "nav.integrations": "Integrations",
  "nav.users": "Users",
  "nav.users.all": "All Users",
  "nav.content": "Content",
  "nav.content.conversations": "Conversations",
  "nav.content.storage": "Storage",
  "nav.content.summary_jobs": "Summary Jobs",
  "nav.content.deliveries": "Deliveries",
  "nav.revenue": "Revenue",
  "nav.revenue.billing": "Billing",
  "nav.governance": "Governance",
  "nav.governance.relations": "Relations",
  "nav.governance.blocks": "Blocks",
  "nav.governance.agents": "Agents",
  "nav.governance.grants": "Grants",
  "nav.governance.audit_log": "Audit Log",
  "nav.system": "System",
  "nav.system.health": "Health",
  "nav.system.crashes": "Crashes",

  // ─── Login ───
  "login.title": "Hashee Ops Console",
  "login.subtitle": "Sign in with your admin account",
  "login.email": "Email",
  "login.password": "Password",
  "login.signing_in": "Signing in...",
  "login.sign_in": "Sign in",
  "login.dismiss_error": "Dismiss error",

  // ─── Dashboard ───
  "dashboard.title": "Dashboard",
  "dashboard.description": "System overview and key metrics",
  "dashboard.humans": "Humans",
  "dashboard.paid_users": "Paid Users",
  "dashboard.summary_failed_24h": "Summary Failed (24h)",
  "dashboard.system_status": "System Status",
  "dashboard.status_healthy": "Healthy",
  "dashboard.status_degraded": "Degraded",
  "dashboard.status_down": "Down",
  "dashboard.of_total": "of total",
  "dashboard.alerts": "Alerts",
  "dashboard.no_active_alerts": "No active alerts",
  "dashboard.failed_summary_jobs": "failed summary job(s) in the last 24 hours",
  "dashboard.view_summary_jobs": "View Summary Jobs",
  "dashboard.is_status": "is",
  "dashboard.cron_prefix": "Cron",
  "dashboard.poison_messages": "poison message(s) in queue",
  "dashboard.view_system_health": "View System Health",
  "dashboard.messages_today": "Messages (Today)",
  "dashboard.relations": "Relations",
  "dashboard.conversations": "Conversations",
  "dashboard.message_distribution": "Message Distribution (30d)",
  "dashboard.human_messages": "Human",
  "dashboard.agent_messages": "Agent",
  "dashboard.system_messages": "System",
  "dashboard.vs_last_week": "vs last week: {{value}}",

  // ─── Status Bar ───
  "dashboard.status_alerts": "{{count}} alert(s)",
  "dashboard.status_anomalous": "{{count}} KPI anomalous",
  "dashboard.status_normal": "{{count}} KPI normal",
  "dashboard.status_all_healthy": "All healthy",

  // ─── Dashboard Alerts ─��─
  "dashboard.all_systems_healthy": "All systems healthy",
  "dashboard.runbook_prefix": "Runbook:",

  // ─── Dashboard KPI Labels ───
  "dashboard.kpi_new_users": "New Users",
  "dashboard.kpi_new_agents": "New Agents",
  "dashboard.kpi_dau": "DAU",
  "dashboard.kpi_mrr": "MRR",
  "dashboard.group_label_short": "Group",

  // ─── Dashboard Social Trends ───
  "dashboard.social_trends_title": "Social Trends (30d)",
  "dashboard.dim_by_type": "By Type",
  "dashboard.dim_by_agent": "By Top Agent",
  "dashboard.dim_by_country": "By Country",
  "dashboard.h2a_label": "H2A Messages",
  "dashboard.h2h_label": "H2H Messages",
  "dashboard.group_label": "Group Messages",
  "dashboard.funnel_title": "Core Funnel",

  // ─── Funnel Mini ───
  "funnel.step_register": "Register",
  "funnel.step_conversation": "Conversation",
  "funnel.step_summary": "Summary",
  "funnel.step_delivery": "Delivery",
  "funnel.step_share": "Share",
  "funnel.step_storage": "Storage",
  "funnel.step_payment": "Payment",
  "funnel.conversion_rate": "\u2193 {{percent}}%",
  "funnel.empty_hint":
    "Funnel data is computed daily by cron. If empty, check System > Health for cron status.",

  // ─── Trend Card ───
  "trend.up": "\u2191",
  "trend.down": "\u2193",
  "trend.flat": "\u2014",

  // ─── Growth Trend Chart ───
  "growth_trend.title": "Growth Trend (30 days)",
  "growth_trend.chart_label": "Growth trend chart showing DAU and new users",
  "growth_trend.chart_title": "Growth Trend",
  "growth_trend.dau": "DAU",
  "growth_trend.new_users": "New Users",

  // ─── Suggestions ───
  "suggestions.title": "Suggestions",
  "suggestions.loading": "Loading suggestions...",
  "suggestions.ops": "Ops Suggestions",
  "suggestions.product": "Product Suggestions",
  "suggestions.no_suggestions": "No suggestions at this time",

  // ─── Confirm Dialog ───
  "confirm.reason_label": "Reason (minimum {{min}} characters)",
  "confirm.reason_placeholder": "Explain why this action is necessary...",
  "confirm.chars_remaining_one": "{{count}} more character required",
  "confirm.chars_remaining_other": "{{count}} more characters required",

  // ─── Pagination (DataTable) ───
  "pagination.prev": "Prev",
  "pagination.next": "Next",

  // ─── Table (TableParts) ───
  "table.loading": "Loading...",

  // ─── Global Filters ───
  "filters.platform": "Platform",
  "filters.country": "Country",
  "filters.language": "Language",
  "filters.all_platforms": "All Platforms",
  "filters.all_countries": "All Countries",
  "filters.all_languages": "All Languages",

  // ─── Not Found ───
  "not_found.title": "404",
  "not_found.text": "Page not found",
  "not_found.back": "Back to Dashboard",

  // ─── Placeholder ───
  "placeholder.management": "management",
  "placeholder.wave2": "This page will be implemented in Wave 2.",

  // ─── Users ───
  "users.title": "Users",
  "users.description": "Manage user accounts",
  "users.search_placeholder": "Search by name, email, or ID...",
  "users.all_status": "All Status",
  "users.all_plans": "All Plans",
  "users.no_users_found": "No users found.",
  "users.loading": "Loading...",
  "users.col_name": "Name",
  "users.col_email": "Email",
  "users.col_custom_id": "Custom ID",
  "users.col_plan": "Plan",
  "users.col_tokens": "Tokens",
  "users.col_storage": "Storage",
  "users.col_status": "Status",
  "users.col_created": "Created",
  "users.page_info": "{{total}} user(s) \u00B7 Page {{page}} of {{totalPages}}",

  // ─── User Detail ───
  "user_detail.loading": "Loading...",
  "user_detail.not_found": "User not found.",
  "user_detail.failed_to_load": "Failed to load user",
  "user_detail.deactivated": "Deactivated",
  "user_detail.freeze_account": "Freeze Account",
  "user_detail.unfreeze_account": "Unfreeze Account",
  "user_detail.freeze_title": "Freeze Account",
  "user_detail.unfreeze_title": "Unfreeze Account",
  "user_detail.freeze_desc":
    "This will immediately revoke all sessions and disconnect the user.",
  "user_detail.unfreeze_desc":
    "This will restore the user's access. They will need to log in again.",
  "user_detail.user_id": "User ID",
  "user_detail.custom_id": "Custom ID",
  "user_detail.status": "Status",
  "user_detail.created": "Created",
  "user_detail.tab_overview": "Overview",
  "user_detail.tab_summary_jobs": "Summary Jobs",
  "user_detail.tab_audit": "Audit",
  "user_detail.tab_billing": "Billing",
  "user_detail.plan": "Plan",
  "user_detail.token_balance": "Token Balance",
  "user_detail.storage": "Storage",
  "user_detail.devices": "Devices",
  "user_detail.no_summary_jobs": "No summary jobs.",
  "user_detail.no_audit_records": "No audit records.",
  "user_detail.no_billing_events": "No billing events.",
  "user_detail.col_status": "Status",
  "user_detail.col_target": "Target",
  "user_detail.col_created": "Created",
  "user_detail.col_action": "Action",
  "user_detail.col_type": "Type",
  "user_detail.col_amount": "Amount",

  // ─── Agents ───
  "agents.title": "Agents",
  "agents.description": "Agent governance management",
  "agents.search_placeholder": "Search by name or creator...",
  "agents.all_status": "All Status",
  "agents.no_agents_found": "No agents found.",
  "agents.loading": "Loading...",
  "agents.col_name": "Name",
  "agents.col_creator": "Creator",
  "agents.col_connection": "Connection",
  "agents.col_status": "Status",
  "agents.col_risk": "Risk",
  "agents.col_reports": "Reports",
  "agents.col_created": "Created",
  "agents.failed_to_load": "Failed to load agents",

  // ─── Agent Detail ───
  "agent_detail.created_by": "Created by {{name}}",
  "agent_detail.loading": "Loading...",
  "agent_detail.not_found": "Agent not found.",
  "agent_detail.failed_to_load": "Failed to load agent",
  "agent_detail.banned": "Banned",
  "agent_detail.restrict_agent": "Restrict Agent",
  "agent_detail.restore_agent": "Restore Agent",
  "agent_detail.restrict_title": "Restrict Agent",
  "agent_detail.restore_title": "Restore Agent",
  "agent_detail.restrict_desc":
    "This will restrict the agent from operating. Existing connections will be affected.",
  "agent_detail.restore_desc": "This will restore the agent to active status.",
  "agent_detail.agent_id": "Agent ID",
  "agent_detail.status": "Status",
  "agent_detail.connection": "Connection",
  "agent_detail.risk_score": "Risk Score",
  "agent_detail.reports": "Reports",
  "agent_detail.description": "Description",
  "agent_detail.created": "Created",
  "agent_detail.h2a_active": "Active",
  "agent_detail.h2a_suspended": "Suspended",
  "agent_detail.h2a_terminated": "Terminated",
  "agent_detail.tab_overview": "Overview",
  "agent_detail.tab_governance_history": "Governance History",
  "agent_detail.tab_reports": "Reports",
  "agent_detail.connection_mode": "Connection Mode",
  "agent_detail.report_count": "Report Count",
  "agent_detail.last_updated": "Last Updated",
  "agent_detail.no_governance_history": "No governance history.",
  "agent_detail.no_reports": "No reports.",
  "agent_detail.col_reporter": "Reporter",
  "agent_detail.col_reason": "Reason",
  "agent_detail.col_status": "Status",
  "agent_detail.col_date": "Date",

  // ─── Grants ───
  "grants.title": "Token Grants",
  "grants.description": "Data access grants",
  "grants.placeholder_user_id": "user_id",
  "grants.placeholder_agent_id": "agent_id",
  "grants.placeholder_scope_type": "scope_type",
  "grants.placeholder_status": "status",
  "grants.no_grants_found": "No grants found",
  "grants.col_grant_id": "Grant ID",
  "grants.col_user": "User",
  "grants.col_agent": "Agent",
  "grants.col_scope": "Scope",
  "grants.col_expires": "Expires",
  "grants.col_status": "Status",
  "grants.never": "Never",
  "grants.loading_detail": "Loading detail...",
  "grants.scope_value": "Scope value:",
  "grants.created": "Created:",
  "grants.summary_jobs": "Summary Jobs",
  "grants.no_summary_jobs": "No associated summary jobs",

  // ─── Audit ───
  "audit.title": "Audit Log",
  "audit.description": "Administrative action history",
  "audit.placeholder_action": "action",
  "audit.placeholder_actor_id": "actor_id",
  "audit.placeholder_target_id": "target_id",
  "audit.start_date": "Start date",
  "audit.end_date": "End date",
  "audit.col_action": "Action",
  "audit.col_actor": "Actor",
  "audit.col_target": "Target",
  "audit.col_metadata": "Metadata",
  "audit.col_date": "Date",
  "audit.no_records_found": "No audit records found",

  // ─── Metrics Layout ───
  "metrics.title": "Metrics",
  "metrics.description": "Analytics and performance data",
  "metrics.tab_growth": "Growth",
  "metrics.tab_funnel": "Funnel",
  "metrics.tab_revenue": "Revenue",
  "metrics.tab_system": "System",
  "metrics.tab_behavior": "Behavior",
  "metrics.tab_messages": "Messages",
  "metrics.tab_feature_value": "Feature Value",
  "metrics.tab_segments": "Segments",
  "metrics.tab_cohort": "Cohort",

  // ─── Growth Tab ───
  "growth.avg_dau": "Avg DAU",
  "growth.new_users": "New Users",
  "growth.messages": "Messages",
  "growth.summary_requests": "Summary Requests",
  "growth.daily_breakdown": "Daily Breakdown",
  "growth.col_date": "Date",
  "growth.col_dau": "DAU",
  "growth.col_new_users": "New Users",
  "growth.col_messages": "Messages",
  "growth.col_summary_req": "Summary Req",

  // ─── Funnel Tab ───
  "funnel.funnel_steps": "Funnel Steps",
  "funnel.no_funnel_data": "No funnel data available",
  "funnel.label_funnel": "Funnel",
  "funnel.activation": "Activation",
  "funnel.content_loop": "Content Loop",
  "funnel.monetization": "Monetization",

  // ─── Revenue Tab ───
  "revenue.mrr": "MRR",
  "revenue.paid_users": "Paid Users",
  "revenue.checkouts": "Checkouts",
  "revenue.topups": "Top-ups",
  "revenue.daily_breakdown": "Daily Breakdown",
  "revenue.col_date": "Date",
  "revenue.col_mrr": "MRR",
  "revenue.col_paid_users": "Paid Users",
  "revenue.col_checkouts": "Checkouts",
  "revenue.col_topups": "Top-ups",

  // ─── System Tab ───
  "system_tab.summary_requests": "Summary Requests",
  "system_tab.success_rate": "Success Rate",
  "system_tab.storage": "Storage",
  "system_tab.webhook_failures": "Webhook Failures",
  "system_tab.daily_breakdown": "Daily Breakdown",
  "system_tab.col_date": "Date",
  "system_tab.col_summary_req": "Summary Req",
  "system_tab.col_success": "Success",
  "system_tab.col_failed": "Failed",
  "system_tab.col_storage": "Storage",
  "system_tab.col_wh_failures": "WH Failures",

  // ─── Behavior Tab ───
  "behavior.sessions_per_user": "Sessions / User / Day",
  "behavior.messages_per_user": "Messages / User / Day",
  "behavior.summaries_per_user": "Summaries / User / Day",
  "behavior.bookmarks_per_user": "Bookmarks / User / Day",
  "behavior.shares_per_user": "Shares / User / Day",
  "behavior.agent_usage_rate": "Agent Usage Rate",
  "behavior.retention_rate": "Retention Rate",
  "behavior.message_breakdown": "Message Breakdown",
  "behavior.human_messages": "Human Messages",
  "behavior.agent_messages": "Agent Messages",
  "behavior.group_messages": "Group Messages",
  "behavior.daily_breakdown": "Daily Breakdown",
  "behavior.col_date": "Date",
  "behavior.col_sessions": "Sessions/U",
  "behavior.col_msgs": "Msgs/U",
  "behavior.col_summaries": "Summaries/U",
  "behavior.col_bookmarks": "Bookmarks/U",
  "behavior.col_shares": "Shares/U",
  "behavior.col_agent_rate": "Agent Rate",
  "behavior.col_retention": "Retention",

  // ─── Feature Value Tab ───
  "feature_value.title": "Feature Value Analysis",
  "feature_value.col_feature": "Feature",
  "feature_value.col_exposure": "Exposure",
  "feature_value.col_adoption": "Adoption",
  "feature_value.col_repeat": "Repeat",
  "feature_value.col_retention_lift": "Retention Lift",
  "feature_value.col_status": "Status",
  "feature_value.core_feature": "Core Feature",
  "feature_value.exposed_but_weak": "Exposed but Weak",
  "feature_value.used_but_shallow": "Used but Shallow",
  "feature_value.neutral": "Neutral",

  // ─── Segment Tab ───
  "segment.distribution": "Segment Distribution",
  "segment.no_data": "No segmentation data for this date",
  "segment.users_title": "Users",
  "segment.power_user": "Power User",
  "segment.builder": "Builder",
  "segment.organizer": "Organizer",
  "segment.reader": "Reader",
  "segment.chat_only": "Chat Only",
  "segment.col_user_id": "User ID",
  "segment.col_messages": "Messages",
  "segment.col_summaries": "Summaries",
  "segment.col_deliveries": "Deliveries",
  "segment.col_shares": "Shares",
  "segment.col_favorited": "Favorited",
  "segment.no_users": "No users in this segment",

  // ─── Cohort Tab ───
  "cohort.cohorts": "Cohorts",
  "cohort.total_users": "Total Users",
  "cohort.avg_week1_retention": "Avg Week 1 Retention",
  "cohort.avg_week4_retention": "Avg Week 4 Retention",
  "cohort.retention_matrix": "Retention Matrix",
  "cohort.col_cohort_week": "Cohort Week",
  "cohort.col_users": "Users",
  "cohort.no_cohort_data": "No cohort data",

  // ─── Billing Page ───
  "billing.title": "Billing",
  "billing.description": "User billing lookup and actions",
  "billing.search_placeholder": "Enter user_id...",
  "billing.user": "User",
  "billing.plan": "Plan",
  "billing.status": "Status",
  "billing.token_balance": "Token Balance",
  "billing.grant_tokens": "Grant Tokens",
  "billing.resync_entitlement": "Resync Entitlement",
  "billing.token_amount_label": "Token amount to grant:",
  "billing.grant_title": "Grant Tokens",
  "billing.resync_title": "Resync Entitlement",
  "billing.replay_title": "Replay Webhook",
  "billing.grant_desc": "Grant {{amount}} tokens to this user.",
  "billing.resync_desc": "Re-sync entitlement from Stripe.",
  "billing.replay_desc": "Replay this failed webhook event.",

  // ─── Billing Tables ───
  "billing_table.token_ledger": "Token Ledger",
  "billing_table.col_type": "Type",
  "billing_table.col_amount": "Amount",
  "billing_table.col_balance_after": "Balance After",
  "billing_table.col_reason": "Reason",
  "billing_table.col_date": "Date",
  "billing_table.no_ledger_entries": "No ledger entries",
  "billing_table.webhook_events": "Webhook Events",
  "billing_table.col_provider": "Provider",
  "billing_table.col_event_type": "Event Type",
  "billing_table.col_status": "Status",
  "billing_table.col_action": "Action",
  "billing_table.replay": "Replay",
  "billing_table.no_webhook_events": "No webhook events",

  // ─── Storage Page ───
  "storage.title": "Storage",
  "storage.description": "Monitor user storage usage and sync status",
  "storage.filter_all": "All",
  "storage.filter_over_quota": "Over Quota",
  "storage.filter_sync_error": "Sync Error",
  "storage.no_users_found": "No users found.",
  "storage.col_user": "User",
  "storage.col_used_limit": "Used / Limit",
  "storage.col_msg": "Msg",
  "storage.col_media": "Media",
  "storage.col_art": "Art",
  "storage.col_kb": "KB",
  "storage.col_sync": "Sync",

  // ─── Storage Detail Panel ───
  "storage_detail.breakdown": "Breakdown",
  "storage_detail.failed_to_load": "Failed to load detail.",
  "storage_detail.recalculate": "Recalculate",
  "storage_detail.run_cleanup": "Run Cleanup",
  "storage_detail.recalculate_title": "Recalculate Storage",
  "storage_detail.recalculate_desc":
    "This will recalculate total storage usage from object sizes. Existing usage value will be overwritten.",
  "storage_detail.cleanup_title": "Run Cleanup",
  "storage_detail.cleanup_desc":
    "This will mark all soft-deleted objects for physical deletion. This action cannot be undone.",

  // ─── Summary Jobs Page ───
  "summary_jobs.title": "Summary Jobs",
  "summary_jobs.description":
    "Monitor AI summarization jobs and retry failures",
  "summary_jobs.filter_all": "All",
  "summary_jobs.filter_pending": "Pending",
  "summary_jobs.filter_running": "Running",
  "summary_jobs.filter_completed": "Completed",
  "summary_jobs.filter_failed": "Failed",
  "summary_jobs.user_id_placeholder": "Filter by user ID...",
  "summary_jobs.no_jobs_found": "No jobs found.",
  "summary_jobs.col_target": "Target",
  "summary_jobs.col_user": "User",
  "summary_jobs.col_status": "Status",
  "summary_jobs.col_model": "Model",
  "summary_jobs.col_tokens_in": "Tokens In",
  "summary_jobs.col_tokens_out": "Tokens Out",
  "summary_jobs.col_created": "Created",

  // ─── Summary Job Detail Panel ───
  "summary_job_detail.failed_to_load": "Failed to load detail.",
  "summary_job_detail.error": "Error",
  "summary_job_detail.grant": "Grant",
  "summary_job_detail.scope": "Scope",
  "summary_job_detail.expires": "Expires",
  "summary_job_detail.thread": "Thread",
  "summary_job_detail.thread_status": "Status",
  "summary_job_detail.preview": "Preview",
  "summary_job_detail.retry_job": "Retry Job",
  "summary_job_detail.retry_title": "Retry Summary Job",
  "summary_job_detail.retry_desc":
    "This will create a new pending job with the same target. The failed job record will remain for audit.",

  // ─── Deliveries Page ───
  "deliveries.title": "Deliveries",
  "deliveries.description": "Manage delivery content",
  "deliveries.all_types": "All Types",
  "deliveries.user_id_placeholder": "Filter by user ID...",
  "deliveries.no_deliveries_found": "No deliveries found.",
  "deliveries.loading_detail": "Loading detail...",
  "deliveries.col_title": "Title",
  "deliveries.col_type": "Type",
  "deliveries.col_user": "User",
  "deliveries.col_conversation": "Conversation",
  "deliveries.col_pinned": "Pinned",
  "deliveries.col_hidden": "Hidden",
  "deliveries.col_deleted": "Deleted",
  "deliveries.col_created": "Created",
  "deliveries.detail_user": "User",
  "deliveries.detail_conversation": "Conversation",
  "deliveries.detail_mime": "MIME",
  "deliveries.detail_size": "Size",
  "deliveries.detail_updated": "Updated",
  "deliveries.reindex": "Reindex",
  "deliveries.unhide": "Unhide",
  "deliveries.unpin": "Unpin",
  "deliveries.reindex_title": "Reindex Delivery",
  "deliveries.unhide_title": "Unhide Delivery",
  "deliveries.unpin_title": "Unpin Delivery",
  "deliveries.reindex_desc":
    "This will trigger re-indexing of the delivery content.",
  "deliveries.unhide_desc":
    "This will make the delivery visible to the user again.",
  "deliveries.unpin_desc": "This will remove the pin from the delivery.",

  // ─── Integrations Page ───
  "integrations.title": "Integrations",
  "integrations.description": "SDK, plugin, and CLI metrics",
  "integrations.tab_sdk": "SDK",
  "integrations.tab_openclaw": "OpenClaw",
  "integrations.tab_claude_code": "Claude Code Plugin",
  "integrations.tab_codex": "Codex Plugin",
  "integrations.tab_cli": "CLI",
  "integrations.active_accounts": "Active Accounts",
  "integrations.active_agents": "Active Agents",
  "integrations.messages": "Messages",
  "integrations.deliveries": "Deliveries",
  "integrations.errors": "Errors",
  "integrations.disconnects": "Disconnects",
  "integrations.version_distribution": "Version Distribution",
  "integrations.col_version": "Version",
  "integrations.col_count": "Count",
  "integrations.col_share": "Share",
  "integrations.no_version_data": "No version data available",

  // ─── Health Page ───
  "health.title": "System Health",
  "health.description": "Component status and background job monitoring",
  "health.components": "Components",
  "health.api": "API",
  "health.neon": "Neon (DB)",
  "health.kv": "KV Store",
  "health.r2": "R2 Storage",
  "health.checked_at": "Checked at",
  "health.queue_stats": "Queue Stats",
  "health.poison_messages": "Poison Messages",
  "health.pending_jobs": "Pending Jobs",
  "health.running_jobs": "Running Jobs",
  "health.failed_24h": "Failed (24h)",
  "health.cron_tasks": "Cron Tasks",
  "health.col_name": "Name",
  "health.col_status": "Status",
  "health.col_detail": "Detail",

  // ─── Crashes ───
  "crashes.title": "Crash Reports",
  "crashes.description":
    "Grouped crash reports from the self-hosted /v1/crash endpoint",
  "crashes.filter_platform": "Platform",
  "crashes.filter_since": "Since",
  "crashes.filter_all": "All",
  "crashes.since_24h": "24h",
  "crashes.since_7d": "7 days",
  "crashes.since_30d": "30 days",
  "crashes.no_groups": "No crashes in this window.",
  "crashes.col_error": "Error",
  "crashes.col_platform": "Platform",
  "crashes.col_count": "Count",
  "crashes.col_first_seen": "First seen",
  "crashes.col_last_seen": "Last seen",
  "crashes.samples_title": "Recent samples",
  "crashes.back_to_list": "Back to list",
  "crashes.sample_stack": "Stack",
  "crashes.sample_breadcrumbs": "Breadcrumbs",
  "crashes.sample_extra": "Extra context",
  "crashes.sample_app_version": "App version",
  "crashes.sample_os_version": "OS version",
  "crashes.sample_is_fatal": "Fatal",
  "crashes.sample_component_stack": "Component stack",
  "crashes.sample_device_hash": "Device hash",
  "crashes.sample_user_hash": "User hash",
  "crashes.sample_client_time": "Client time",
  "crashes.group_count": "{{count}} occurrences",

  // ─── Relations ───
  "relations.title": "Relations",
  "relations.description": "View and audit user relationships",
  "relations.col_type": "Type",
  "relations.col_user_a": "User A",
  "relations.col_user_b": "User B / Agent",
  "relations.col_status": "Status",
  "relations.col_conversation": "Conversation",
  "relations.col_created": "Created",
  "relations.all_types": "All Types",
  "relations.all_status": "All Status",
  "relations.search_placeholder": "Search by name or email...",
  "relations.no_relations": "No relations found.",
  "relations.page_info":
    "{{total}} relation(s) \u00B7 Page {{page}} of {{totalPages}}",

  // ─── Blocks ───
  "blocks.title": "Blocks",
  "blocks.description": "View block records for abuse investigation",
  "blocks.col_blocker": "Blocker",
  "blocks.col_blocked": "Blocked",
  "blocks.col_created": "Created",
  "blocks.blocker_placeholder": "Blocker UUID...",
  "blocks.blocked_placeholder": "Blocked UUID...",
  "blocks.no_blocks": "No blocks found.",
  "blocks.page_info":
    "{{total}} block(s) \u00B7 Page {{page}} of {{totalPages}}",

  // ─── Conversations ───
  "conversations.title": "Conversations",
  "conversations.description": "Diagnose conversation state and members",
  "conversations.col_id": "ID",
  "conversations.col_type": "Type",
  "conversations.col_members": "Members",
  "conversations.col_last_activity": "Last Activity",
  "conversations.col_frozen": "Frozen",
  "conversations.col_terminated": "Terminated",
  "conversations.col_created": "Created",
  "conversations.all_types": "All Types",
  "conversations.all_frozen": "All",
  "conversations.frozen_yes": "Frozen",
  "conversations.frozen_no": "Not Frozen",
  "conversations.all_terminated": "All",
  "conversations.terminated_yes": "Terminated",
  "conversations.terminated_no": "Not Terminated",
  "conversations.search_placeholder": "Conversation ID...",
  "conversations.no_conversations": "No conversations found.",
  "conversations.page_info":
    "{{total}} conversation(s) \u00B7 Page {{page}} of {{totalPages}}",

  // ─── Conversation Detail ───
  "conv_detail.title": "Conversation Detail",
  "conv_detail.info": "Info",
  "conv_detail.members": "Members",
  "conv_detail.message_stats": "Message Statistics",
  "conv_detail.col_member": "Member",
  "conv_detail.col_type": "Type",
  "conv_detail.col_unread": "Unread",
  "conv_detail.col_muted": "Muted",
  "conv_detail.col_pinned": "Pinned",
  "conv_detail.col_hidden": "Hidden",
  "conv_detail.col_joined": "Joined",
  "conv_detail.total_messages": "Total Messages",
  "conv_detail.recalled": "Recalled",
  "conv_detail.by_type": "By Content Type",
  "conv_detail.by_sender": "By Sender Type",
  "conv_detail.not_found": "Conversation not found.",

  // ─── Messages Tab ───
  "messages_tab.title": "Messages",
  "messages_tab.total_messages": "Total Messages",
  "messages_tab.human_ratio": "Human %",
  "messages_tab.agent_ratio": "Agent %",
  "messages_tab.recall_rate": "Recall Rate",
  "messages_tab.daily_trends": "Daily Message Trends",
  "messages_tab.type_distribution": "Content Type Distribution",
  "messages_tab.col_date": "Date",
  "messages_tab.col_human": "Human",
  "messages_tab.col_agent": "Agent",
  "messages_tab.col_system": "System",
  "messages_tab.col_total": "Total",
  "messages_tab.col_content_type": "Content Type",
  "messages_tab.col_count": "Count",
  "messages_tab.col_percentage": "%",

  // ─── Sidebar: Invites ───
  "nav.invites": "Invites",
  "nav.invites.applications": "Applications",
  "nav.invites.codes": "Invite Codes",

  // ─── Applications ───
  "applications.title": "Applications",
  "applications.description": "Manage invite-only registration applications",
  "applications.tab_all": "All",
  "applications.tab_pending": "Pending",
  "applications.tab_approved": "Approved",
  "applications.tab_rejected": "Rejected",
  "applications.col_email": "Email",
  "applications.col_name": "Name",
  "applications.col_role": "Role",
  "applications.col_reason": "Reason",
  "applications.col_status": "Status",
  "applications.col_date": "Date",
  "applications.col_actions": "Actions",
  "applications.approve": "Approve",
  "applications.reject": "Reject",
  "applications.reject_title": "Reject Application",
  "applications.reject_desc":
    "Please provide a reason for rejecting this application.",
  "applications.no_applications": "No applications found.",
  "applications.page_info":
    "{{total}} application(s) \u00B7 Page {{page}} of {{totalPages}}",

  // ─── Invite Codes ───
  "invite_codes.title": "Invite Codes",
  "invite_codes.description": "Manage invite codes for registration",
  "invite_codes.create_code": "Create Code",
  "invite_codes.all_status": "All Status",
  "invite_codes.status_available": "Available",
  "invite_codes.status_claimed": "Claimed",
  "invite_codes.status_expired": "Expired",
  "invite_codes.col_code": "Code",
  "invite_codes.col_issued_by": "Issued By",
  "invite_codes.col_claimed_by": "Claimed By",
  "invite_codes.col_status": "Status",
  "invite_codes.col_created": "Created",
  "invite_codes.col_expires": "Expires",
  "invite_codes.never": "Never",
  "invite_codes.no_codes": "No invite codes found.",
  "invite_codes.page_info":
    "{{total}} code(s) \u00B7 Page {{page}} of {{totalPages}}",

  // ─── Date Picker ───
  "date_picker.date": "Date",
  "date_picker.7d": "7 Days",
  "date_picker.30d": "30 Days",
  "date_picker.90d": "90 Days",
  "date_picker.1y": "1 Year",
  "date_picker.custom": "Custom",
};
